import { useState, FormEvent, ChangeEvent } from 'react';
import { Link } from 'react-router-dom';

const PLANS = [
  {
    id: 'starter',
    name: 'Starter',
    price: 49,
    badge: '',
    popular: false,
    perks: ['California maps & listings', 'Basic deal alerts', '5 saved properties'],
  },
  {
    id: 'pro',
    name: 'Pro',
    price: 99,
    badge: 'Most Popular',
    popular: true,
    perks: ['All 3 states: CA + NV + AZ', 'Priority alerts + deal tracker', '50 saved properties'],
  },
  {
    id: 'elite',
    name: 'Elite',
    price: 199,
    badge: 'Full Service',
    popular: false,
    perks: ['Everything in Pro', '1-on-1 coaching calls', 'Unlimited saves + mastermind'],
  },
];

const TRUST_STATS = [
  { icon: 'ri-group-line', value: '500+', label: 'Active Members' },
  { icon: 'ri-building-2-line', value: '2,847', label: 'Properties Listed' },
  { icon: 'ri-shield-check-line', value: '94%', label: 'Member Success Rate' },
];

type StrengthLevel = 0 | 1 | 2 | 3 | 4;

function getPasswordStrength(password: string): StrengthLevel {
  if (!password) return 0;
  let score = 0;
  if (password.length >= 8) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;
  return score as StrengthLevel;
}

const STRENGTH_LABELS = ['', 'Weak', 'Fair', 'Good', 'Strong'];
const STRENGTH_COLORS = ['', 'bg-red-500', 'bg-amber-500', 'bg-yellow-400', 'bg-green-500'];
const STRENGTH_TEXT   = ['', 'text-red-400', 'text-amber-400', 'text-yellow-400', 'text-green-400'];

export default function SignUp() {
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    plan: 'pro',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm]   = useState(false);
  const [errors, setErrors]             = useState<Record<string, string>>({});
  const [submitting, setSubmitting]     = useState(false);
  const [submitted, setSubmitted]       = useState(false);

  const strength = getPasswordStrength(form.password);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => { const n = { ...prev }; delete n[name]; return n; });
  };

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!form.firstName.trim()) errs.firstName = 'First name is required';
    if (!form.lastName.trim())  errs.lastName  = 'Last name is required';
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      errs.email = 'A valid email is required';
    if (!form.phone.trim())     errs.phone    = 'Phone number is required';
    if (form.password.length < 8) errs.password = 'Password must be at least 8 characters';
    if (form.password !== form.confirmPassword)
      errs.confirmPassword = 'Passwords do not match';
    return errs;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSubmitting(true);
    /* ── Auth will be wired to Supabase once connected ── */
    await new Promise((r) => setTimeout(r, 1200));
    setSubmitting(false);
    setSubmitted(true);
  };

  /* ── Success screen ── */
  if (submitted) {
    const selected = PLANS.find((p) => p.id === form.plan)!;
    return (
      <div className="min-h-screen bg-navy flex items-center justify-center px-6 py-16">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 flex items-center justify-center rounded-full bg-gold/20 border-2 border-gold mx-auto mb-6 animate-fade-in">
            <i className="ri-check-double-line text-gold text-4xl" />
          </div>
          <h1 className="font-playfair text-4xl font-bold text-white mb-3">Welcome aboard!</h1>
          <p className="text-white/60 mb-2">
            Your <span className="text-gold font-semibold">{selected.name}</span> account has been created.
          </p>
          <p className="text-white/40 text-sm mb-8">
            Check your inbox — a confirmation email is on its way to <span className="text-white/70">{form.email}</span>.
          </p>
          <Link
            to="/login"
            className="inline-flex items-center gap-2 px-8 py-4 bg-gold text-navy-dark font-bold rounded-xl hover:bg-gold-light transition-colors whitespace-nowrap"
          >
            Sign In to Your Account <i className="ri-arrow-right-line" />
          </Link>
          <p className="mt-5 text-white/30 text-xs">
            Your 14-day free trial starts today. No charge until the trial ends.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-navy flex flex-col lg:flex-row">

      {/* ── LEFT PANEL — branding ── */}
      <div className="hidden lg:flex lg:w-[42%] flex-col justify-between p-12 xl:p-16 relative overflow-hidden">
        {/* Background texture */}
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/e65384ccf90363abb76b4122d6ae1aa6.jpg"
            alt=""
            className="w-full h-full object-cover object-center opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-navy/80 via-navy/60 to-navy/90" />
        </div>

        <div className="relative z-10 flex flex-col h-full justify-between">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0">
            <img
              src="https://public.readdy.ai/ai/img_res/e4baf86f-3d4f-4e4d-945f-ebc375a864af.png"
              alt="Foreclosure Recovery"
              className="h-14 w-auto object-contain"
            />
          </Link>

          {/* Main copy */}
          <div>
            <span className="inline-block px-3 py-1 border border-gold/40 text-gold text-xs font-semibold tracking-widest uppercase rounded-full mb-6">
              Start Your Free Trial
            </span>
            <h2 className="font-playfair text-4xl xl:text-5xl font-bold text-white leading-tight mb-5">
              Your Edge in the<br />
              <span className="text-gold">Foreclosure Market</span><br />
              Starts Here
            </h2>
            <p className="text-white/55 text-base leading-relaxed max-w-sm">
              Join 500+ investors already accessing exclusive pre-market deals in California, Nevada, and Arizona — 30 to 90 days before public auction.
            </p>

            {/* Stats */}
            <div className="flex gap-8 mt-10">
              {TRUST_STATS.map((s) => (
                <div key={s.label}>
                  <p className="font-playfair text-3xl font-bold text-gold">{s.value}</p>
                  <p className="text-white/40 text-xs mt-0.5">{s.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Testimonial snippet */}
          <div className="bg-white/5 border border-white/10 rounded-2xl p-6 backdrop-blur-sm">
            <div className="text-gold text-4xl font-playfair leading-none mb-3">&ldquo;</div>
            <p className="text-white/80 text-sm leading-relaxed mb-4">
              Within my first 60 days I found a Phoenix property at 58% below market. The platform paid for itself on day one.
            </p>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full overflow-hidden flex-shrink-0">
                <img
                  src="https://readdy.ai/api/search-image?query=professional%20Hispanic%20man%2035s%20confident%20smile%20headshot%20neutral%20background%20portrait&width=80&height=80&seq=signup_avatar01&orientation=squarish"
                  alt="David R."
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <p className="text-white text-sm font-semibold">David R.</p>
                <p className="text-white/40 text-xs">Pro Member · Phoenix, AZ</p>
              </div>
              <span className="ml-auto px-2 py-1 bg-gold/20 text-gold text-xs font-bold rounded-full whitespace-nowrap">
                195% ROI
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* ── RIGHT PANEL — form ── */}
      <div className="flex-1 flex flex-col justify-center px-6 py-12 md:px-12 xl:px-16 overflow-y-auto">

        {/* Mobile logo */}
        <div className="lg:hidden mb-8 flex justify-center">
          <Link to="/">
            <img
              src="https://public.readdy.ai/ai/img_res/e4baf86f-3d4f-4e4d-945f-ebc375a864af.png"
              alt="Foreclosure Recovery"
              className="h-12 w-auto object-contain"
            />
          </Link>
        </div>

        <div className="max-w-[520px] w-full mx-auto">
          <h1 className="font-playfair text-3xl md:text-4xl font-bold text-white mb-1">
            Create Your Account
          </h1>
          <p className="text-white/50 text-sm mb-8">
            14-day free trial · No credit card required · Cancel anytime
          </p>

          <form onSubmit={handleSubmit} className="flex flex-col gap-5" noValidate>

            {/* ── Name row ── */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-white/50 uppercase tracking-wide mb-2">
                  First Name <span className="text-gold">*</span>
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={form.firstName}
                  onChange={handleChange}
                  placeholder="John"
                  autoComplete="given-name"
                  className={`w-full px-4 py-3 rounded-xl bg-white/5 border text-white text-sm placeholder-white/20 focus:outline-none focus:border-gold transition-colors ${
                    errors.firstName ? 'border-red-500' : 'border-white/15 hover:border-white/30'
                  }`}
                />
                {errors.firstName && <p className="text-red-400 text-xs mt-1">{errors.firstName}</p>}
              </div>
              <div>
                <label className="block text-xs font-semibold text-white/50 uppercase tracking-wide mb-2">
                  Last Name <span className="text-gold">*</span>
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={form.lastName}
                  onChange={handleChange}
                  placeholder="Smith"
                  autoComplete="family-name"
                  className={`w-full px-4 py-3 rounded-xl bg-white/5 border text-white text-sm placeholder-white/20 focus:outline-none focus:border-gold transition-colors ${
                    errors.lastName ? 'border-red-500' : 'border-white/15 hover:border-white/30'
                  }`}
                />
                {errors.lastName && <p className="text-red-400 text-xs mt-1">{errors.lastName}</p>}
              </div>
            </div>

            {/* ── Email ── */}
            <div>
              <label className="block text-xs font-semibold text-white/50 uppercase tracking-wide mb-2">
                Email Address <span className="text-gold">*</span>
              </label>
              <div className="relative">
                <i className="ri-mail-line absolute left-4 top-1/2 -translate-y-1/2 text-white/30 text-sm" />
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="john@example.com"
                  autoComplete="email"
                  className={`w-full pl-10 pr-4 py-3 rounded-xl bg-white/5 border text-white text-sm placeholder-white/20 focus:outline-none focus:border-gold transition-colors ${
                    errors.email ? 'border-red-500' : 'border-white/15 hover:border-white/30'
                  }`}
                />
              </div>
              {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
            </div>

            {/* ── Phone ── */}
            <div>
              <label className="block text-xs font-semibold text-white/50 uppercase tracking-wide mb-2">
                Phone Number <span className="text-gold">*</span>
              </label>
              <div className="relative">
                <i className="ri-phone-line absolute left-4 top-1/2 -translate-y-1/2 text-white/30 text-sm" />
                <input
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  placeholder="(555) 000-0000"
                  autoComplete="tel"
                  className={`w-full pl-10 pr-4 py-3 rounded-xl bg-white/5 border text-white text-sm placeholder-white/20 focus:outline-none focus:border-gold transition-colors ${
                    errors.phone ? 'border-red-500' : 'border-white/15 hover:border-white/30'
                  }`}
                />
              </div>
              {errors.phone && <p className="text-red-400 text-xs mt-1">{errors.phone}</p>}
            </div>

            {/* ── Password ── */}
            <div>
              <label className="block text-xs font-semibold text-white/50 uppercase tracking-wide mb-2">
                Password <span className="text-gold">*</span>
              </label>
              <div className="relative">
                <i className="ri-lock-line absolute left-4 top-1/2 -translate-y-1/2 text-white/30 text-sm" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={form.password}
                  onChange={handleChange}
                  placeholder="Min. 8 characters"
                  autoComplete="new-password"
                  className={`w-full pl-10 pr-11 py-3 rounded-xl bg-white/5 border text-white text-sm placeholder-white/20 focus:outline-none focus:border-gold transition-colors ${
                    errors.password ? 'border-red-500' : 'border-white/15 hover:border-white/30'
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/70 transition-colors cursor-pointer"
                >
                  <i className={`ri-eye${showPassword ? '-off' : ''}-line text-sm`} />
                </button>
              </div>
              {/* Strength bar */}
              {form.password.length > 0 && (
                <div className="mt-2">
                  <div className="flex gap-1 mb-1">
                    {[1, 2, 3, 4].map((lvl) => (
                      <div
                        key={lvl}
                        className={`h-1 flex-1 rounded-full transition-all duration-300 ${
                          strength >= lvl ? STRENGTH_COLORS[strength] : 'bg-white/10'
                        }`}
                      />
                    ))}
                  </div>
                  <p className={`text-xs font-medium ${STRENGTH_TEXT[strength]}`}>
                    {STRENGTH_LABELS[strength]} password
                  </p>
                </div>
              )}
              {errors.password && <p className="text-red-400 text-xs mt-1">{errors.password}</p>}
            </div>

            {/* ── Confirm Password ── */}
            <div>
              <label className="block text-xs font-semibold text-white/50 uppercase tracking-wide mb-2">
                Confirm Password <span className="text-gold">*</span>
              </label>
              <div className="relative">
                <i className="ri-lock-2-line absolute left-4 top-1/2 -translate-y-1/2 text-white/30 text-sm" />
                <input
                  type={showConfirm ? 'text' : 'password'}
                  name="confirmPassword"
                  value={form.confirmPassword}
                  onChange={handleChange}
                  placeholder="Re-enter your password"
                  autoComplete="new-password"
                  className={`w-full pl-10 pr-11 py-3 rounded-xl bg-white/5 border text-white text-sm placeholder-white/20 focus:outline-none focus:border-gold transition-colors ${
                    errors.confirmPassword ? 'border-red-500'
                    : form.confirmPassword && form.confirmPassword === form.password ? 'border-green-500'
                    : 'border-white/15 hover:border-white/30'
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm((v) => !v)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/70 transition-colors cursor-pointer"
                >
                  <i className={`ri-eye${showConfirm ? '-off' : ''}-line text-sm`} />
                </button>
                {form.confirmPassword && form.confirmPassword === form.password && (
                  <div className="absolute right-10 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                    <i className="ri-check-line text-green-400 text-sm" />
                  </div>
                )}
              </div>
              {errors.confirmPassword && <p className="text-red-400 text-xs mt-1">{errors.confirmPassword}</p>}
            </div>

            {/* ── Plan Selection ── */}
            <div>
              <label className="block text-xs font-semibold text-white/50 uppercase tracking-wide mb-3">
                Choose Your Plan <span className="text-gold">*</span>
              </label>
              <div className="flex flex-col gap-3">
                {PLANS.map((plan) => {
                  const selected = form.plan === plan.id;
                  return (
                    <label
                      key={plan.id}
                      className={`flex items-start gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                        selected
                          ? plan.popular
                            ? 'border-gold bg-gold/10'
                            : 'border-white/40 bg-white/8'
                          : 'border-white/10 bg-white/3 hover:border-white/25'
                      }`}
                    >
                      {/* Custom radio */}
                      <div className="flex-shrink-0 mt-0.5">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                          selected
                            ? plan.popular ? 'border-gold' : 'border-white/60'
                            : 'border-white/25'
                        }`}>
                          {selected && (
                            <div className={`w-2.5 h-2.5 rounded-full ${plan.popular ? 'bg-gold' : 'bg-white/70'}`} />
                          )}
                        </div>
                      </div>
                      <input
                        type="radio"
                        name="plan"
                        value={plan.id}
                        checked={selected}
                        onChange={handleChange}
                        className="sr-only"
                      />

                      {/* Plan info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`font-semibold text-sm ${selected ? 'text-white' : 'text-white/60'}`}>
                            {plan.name}
                          </span>
                          {plan.badge && (
                            <span className={`px-2 py-0.5 text-xs font-bold rounded-full whitespace-nowrap ${
                              plan.popular ? 'bg-gold text-navy-dark' : 'bg-white/10 text-white/50'
                            }`}>
                              {plan.badge}
                            </span>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1.5">
                          {plan.perks.map((perk) => (
                            <span key={perk} className="flex items-center gap-1 text-xs text-white/40">
                              <i className="ri-check-line text-gold/60 text-xs" />
                              {perk}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* Price */}
                      <div className="text-right flex-shrink-0">
                        <span className={`font-playfair text-xl font-bold ${plan.popular ? 'text-gold' : 'text-white/70'}`}>
                          ${plan.price}
                        </span>
                        <span className="text-white/30 text-xs">/mo</span>
                      </div>
                    </label>
                  );
                })}
              </div>
              <p className="text-white/30 text-xs mt-2">
                Free for 14 days · Then charged monthly · Cancel anytime
              </p>
            </div>

            {/* ── Terms ── */}
            <p className="text-white/30 text-xs leading-relaxed">
              By creating an account you agree to our{' '}
              <Link to="/contact" className="text-gold/70 hover:text-gold underline">Terms of Service</Link>
              {' '}and{' '}
              <Link to="/contact" className="text-gold/70 hover:text-gold underline">Privacy Policy</Link>.
            </p>

            {/* ── Submit ── */}
            <button
              type="submit"
              disabled={submitting}
              className="w-full flex items-center justify-center gap-3 py-4 bg-gold text-navy-dark font-bold text-base rounded-xl hover:bg-gold-light transition-all duration-200 disabled:opacity-60 whitespace-nowrap cursor-pointer mt-1"
            >
              {submitting ? (
                <>
                  <i className="ri-loader-4-line animate-spin text-lg" />
                  Creating Your Account…
                </>
              ) : (
                <>
                  <i className="ri-user-add-line text-lg" />
                  Create My Account
                </>
              )}
            </button>
          </form>

          {/* ── Bottom links ── */}
          <div className="mt-8 pt-6 border-t border-white/10 text-center flex flex-col gap-2">
            <p className="text-white/40 text-sm">
              Already have an account?{' '}
              <Link to="/login" className="text-gold font-semibold hover:text-gold-light transition-colors">
                Sign In
              </Link>
            </p>
            <Link
              to="/pricing"
              className="text-white/30 text-xs hover:text-white/60 transition-colors"
            >
              View full plan comparison →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
